import { Injectable } from '@nestjs/common';
import { InjectDataSource } from '@nestjs/typeorm';
import { BarcodeTypeEnum, GlobalResponseObject, OperationCategoryEnum, PoStatusEnum } from '@shahi-analytics/shared-models';
import { DataSource, Repository } from 'typeorm';
import { BuyerEntity } from '../entities/buyer.entity';
import { DcEntity } from '../entities/dc.entity';
import { MigrationLogEntity } from '../entities/migration-log.entity';
import { OperationEntity } from '../entities/operation.entity';
import { PoHeaderEntity } from '../entities/po-header.entity';
import { PoLineEntity } from '../entities/po-line.entity';
import { ScheduleEntity } from '../entities/schedule.entity';
import { StyleOperationEntity } from '../entities/style-op.entity';
import { TrackingInfoEntity } from '../entities/tracking-info.entity';
import { DbNamesEnum } from './db-connection/db-names.enum';
import { GtsDbConnectionsService } from './db-connection/shahi-db-connection';
import { GenericTransactionManager } from '../../database/typeorm-transactions';
import { GtsAppTableNamesEnum } from '../gts/models/app-table-names.enum';
import { TableMigrationStatusEnum } from '../gts/models/table-migration-status.enum';
import { GtsMigrationHelperService } from './migration-helper.service';
import { ErrorResponse } from '@shahi-analytics/backend-utils';
import { ScheduleDcEntity } from '../entities/schedule-dc.entity';



@Injectable()
export class GtsMigrationService {
  private opEnt: Repository<OperationEntity>;
  private poHeaderEnt: Repository<PoHeaderEntity>;
  private poLineEnt: Repository<PoLineEntity>;
  private schEnt: Repository<ScheduleEntity>;
  private trackInfoEnt: Repository<TrackingInfoEntity>;
  private styleOpEnt: Repository<StyleOperationEntity>;
  private dcEnt: Repository<DcEntity>
  private migLogEnt: Repository<MigrationLogEntity>
  private schDcEnt: Repository<ScheduleDcEntity>

  constructor(
    private dataSource: DataSource,
    private transManager: GenericTransactionManager,
    private gtsDb: GtsDbConnectionsService,
    private helperService: GtsMigrationHelperService
  ) { 
    this.opEnt = dataSource.getRepository(OperationEntity);
    this.poHeaderEnt = dataSource.getRepository(PoHeaderEntity);
    this.poLineEnt = dataSource.getRepository(PoLineEntity);
    this.schEnt = dataSource.getRepository(ScheduleEntity);
    this.trackInfoEnt = dataSource.getRepository(TrackingInfoEntity);
    this.styleOpEnt = dataSource.getRepository(StyleOperationEntity);
    this.migLogEnt = dataSource.getRepository(MigrationLogEntity);
    this.dcEnt = dataSource.getRepository(DcEntity);
    this.schDcEnt = dataSource.getRepository(ScheduleDcEntity);
  }
  
  /**
   * Validated for phase 2
   * INITIAL SET UP WHEN CONFIGURING A NEW PLANT. A one time master data dump creation
   * creates the operations
   * @param unitId 
   * @param date 
   * @param hour 
   * @returns 
   */
  async createOperations(unitId: string, date: string, hour: number): Promise<GlobalResponseObject> {
    try{ 
      // get all the existing operations
      const operations = await this.opEnt.find({select: ['opCode', 'opSequence', 'extRefCode'], where: {unitId: unitId, isActive: true}});
      const existingOps = new Set<string>();
      operations.forEach(r => {
        existingOps.add(r.extRefCode);
      });
      const opEntities: OperationEntity[] = [];
      // get all the operations from the GTS
      const gtsDbConn = await this.gtsDb.getGtsDbConnectionForUnit(DbNamesEnum.SEPL_SCAN, unitId);
      // get the ops from the GTS
      const query = `Select ms.SL_Name as op_name, ms.SL_ID as op_code, ms.SL_IndexNo as op_sequence, ms.Department  as op_category FROM dbo.m_SL ms  With (NOLOCK) where active = 'Y' `;
      const result: {op_name: string,	op_code: string, op_sequence: number, op_category: string}[] = await gtsDbConn.query(query);

      for(const rec of result) {
        if(!existingOps.has(rec.op_code.toString())) {
          const opEntity = new OperationEntity();
          opEntity.opCategory = this.getOpCategory(rec.op_category);
          opEntity.opCode = rec.op_code;
          opEntity.opName = rec.op_name;
          opEntity.opSequence = rec.op_sequence;
          opEntity.extRefCode = rec.op_code;
          opEntity.unitId = unitId;
          opEntities.push(opEntity);
        }
      }
      if(opEntities.length > 0) {
        await this.opEnt.save(opEntities, { reload: false});
      }
      // call the style operations creation
      return new GlobalResponseObject(true, 0, 'Operations successfully added');
    } catch (error) {
      throw error;
    }
  }

  // helper
  getOpCategory(opCategory: string): OperationCategoryEnum {
    switch(opCategory) {
      case 'CUTTING': return OperationCategoryEnum.CUTTING; break;
      case 'SEWING': return OperationCategoryEnum.SEWING; break;
      case 'WASHING': return OperationCategoryEnum.WASHING; break;
      case 'FINISHING': return OperationCategoryEnum.FINISHING; break;
      case 'FINISHING': return OperationCategoryEnum.PACKING; break;
    }
  }


  // Validated for phase 2
  async createStylesOps(unitId: string): Promise<GlobalResponseObject> {
    const stylesArray = [];
    const styles: {style: string}[] = await this.trackInfoEnt.createQueryBuilder('t').select('DISTINCT style').getRawMany();
    styles.forEach(r => {
      stylesArray.push(r.style);
    })
    await this.createStyleOperations(unitId, stylesArray);
    return new GlobalResponseObject(true, 0, 'DONE');
  }

  /**
   * Validated for phase 2
   * @param unitId 
   * @param style 
   * @returns 
   */
  async createStyleOperations(unitId: string, styles: string[]): Promise<GlobalResponseObject> {
    try {
      const styleOpEntities: StyleOperationEntity[] = [];
      // check if the style ops are already populated
      for(const style of styles) {
        const styleOps = await this.styleOpEnt.find({ select: ['style'], where: {style: style, unitId: unitId}});
        if(styleOps.length > 0) {
          return new GlobalResponseObject(true, 0, 'Style Operations are already added'); 
        }
        // create the style operations
        const operations = await this.opEnt.find({select: ['opCode', 'opSequence', 'extRefCode', 'opCategory'], where: {unitId: unitId, isActive: true}});
        for(const rec of operations) {
          const styleOpEnt = new StyleOperationEntity();
          styleOpEnt.opCategory = rec.opCategory;
          styleOpEnt.opCode = rec.opCode;
          styleOpEnt.opSequence = rec.opSequence;
          styleOpEnt.style = style;
          styleOpEnt.unitId = unitId;
          styleOpEntities.push(styleOpEnt);
        }
      }
      await this.styleOpEnt.save(styleOpEntities);
      return new GlobalResponseObject(true, 0, 'Style Operations successfully added'); 
    } catch (error) {

    }
  }

  /**
   * 
   * @param unitId 
   * @param date 
   * @param hour 
   * @param fromMinute 
   * @param toMinute 
   * @returns 
   */
  async createPoAndPoLines(unitId: string, date: string, hour: number, ntd: boolean, fromMinute?: string, toMinute?: string): Promise<GlobalResponseObject> {
    let transactionManager = new GenericTransactionManager(this.dataSource);
    try {
      const stylesSet = new Set<string>();
      // get the po info from the PROFIT DB
      const profitDbConn = await this.gtsDb.getGtsDbConnectionForUnit(DbNamesEnum.PROFIT_DB, unitId);

      const alreadyMigrated = await this.helperService.checkIfMigrationIsDoneForTableDateAndHour(date, hour, unitId, GtsAppTableNamesEnum.M_PO);
      if(alreadyMigrated) {
        return new GlobalResponseObject(false, 0, GtsAppTableNamesEnum.M_PO + 'Table already migrated for the date + hour ' + date + ' : ' + hour);
      }
      await this.saveMigrationLog(date, hour, unitId, GtsAppTableNamesEnum.M_PO);

      const query = ` SELECT DcNo as dc_no, ItemID  as product_sku, styleID as style, PONo as po_number, styleName as style_desc, Color as color, 
        Destination  as destination, CAST(ExFactoryDate as varchar) as ex_factory,
        BuyerId  as buyer,  count(1) as quantity
        FROM dbo.T_BundlePieces tbp With (NOLOCK) WHERE tbp.DCIssueDate  = '${date}'
        GROUP BY DCNo, ItemID, styleID, Color, PONo, BuyerId, Destination, styleName, ExFactoryDate`;
      const result: {dc_no: string, product_sku: string, style: string, color: string, po_number: string, buyer: string, destination: string, style_desc: string, ex_factory: string, quantity: number, }[] = await profitDbConn.query(query);
      console.log(query);
      // now loop all the records and calculate the DC quantity
      const dcQtyMap = new Map<string, number>();
      const dcPoColExFactQtyMap = new Map<string, number>();
      result?.forEach(r => {
        const key = r.po_number+'@'+r.style+'@'+r.color+'@'+r.ex_factory+'@'+r.destination;
        if(!dcQtyMap.has(r.dc_no)) {
          dcQtyMap.set(r.dc_no, 0);
        }
        if(!dcPoColExFactQtyMap.has(key)) {
          dcPoColExFactQtyMap.set(key, 0);
        }
        const preQty = dcQtyMap.get(r.dc_no);
        dcQtyMap.set(r.dc_no, Number(r.quantity) + preQty);

        const prePoColExFactQty = dcPoColExFactQtyMap.get(key);
        dcPoColExFactQtyMap.set(key, Number(r.quantity) + prePoColExFactQty);
      });
      console.log(dcPoColExFactQtyMap);

      await transactionManager.startTransaction();
      const buyerEntRepo = transactionManager.getRepository(BuyerEntity);
      const poHeaderEntRepo = transactionManager.getRepository(PoHeaderEntity);
      const poLineEntRepo = transactionManager.getRepository(PoLineEntity);
      const poSchEntRepo = transactionManager.getRepository(ScheduleEntity);
      const dcEntRepo = transactionManager.getRepository(DcEntity);
      const schDcRepo = transactionManager.getRepository(ScheduleDcEntity);

      // DC PART BEGIN
      // now if the dc qty is matching the qty as in the log records...
      const dcEntities: DcEntity[] = [];
      for(const [dc, qty] of dcQtyMap) {
        const dcRec = await this.dcEnt.findOne({ where: { dcNo: dc, unitId: unitId }});
        if(!dcRec) {
          const dcEntity = new DcEntity();
          dcEntity.dcNo = dc; dcEntity.dcDate = date; dcEntity.unitId = unitId; dcEntity.dcQuantity = qty;
          dcEntities.push(dcEntity);
        } else {
          // check if the qty is equaling the dc quantity. If equaling, do nothing. else get the last schedule and increase the diff dc quantity
          const oldDcQty = Number(dcRec.dcQuantity);
          const diffQty = Number(qty) - oldDcQty;
          if(diffQty > 0) {
            await dcEntRepo.update({dcNo: dc,unitId: unitId }, { dcQuantity: () => `dc_quantity + ${Number(diffQty)}`});
          }
        }
      }
      if(dcEntities.length > 0) {
        await dcEntRepo.save(dcEntities, {reload: false});
      }
      // DC PART END


      // PO and PO LINES BEGIN
      for(const rec of result) {
        stylesSet.add(rec.style);
        let buyerId = 0;
        // check if the buyer exist
        const buyer = await buyerEntRepo.findOne({ select: ['id'], where: {buyerName: rec.buyer, unitId: unitId} });
        if(!buyer) {
          const buyerEnt = new BuyerEntity();
          buyerEnt.buyerCode = rec.buyer;
          buyerEnt.buyerDesc = rec.buyer;
          buyerEnt.buyerName = rec.buyer;
          buyerEnt.unitId = unitId;
          const savedBuyer = await buyerEntRepo.save(buyerEnt);
          buyerId = savedBuyer.id;
        } else {
          buyerId = buyer.id;
        }


        // check if the po number already exist
        const poRec = await poHeaderEntRepo.findOne({select: ['id', 'poNumber'], where: {poNumber: rec.po_number, unitId: unitId}});
        if(poRec) {
          const poHeaderId = poRec.id;
          // now check if the PO line exist based on the destination and ex-factory
          const poLine = await poLineEntRepo.findOne({ select: ['id', 'deliveryDate', 'color', 'quantity', 'destination'], where: {poHeaderId: poHeaderId, destination: rec.destination, deliveryDate: rec.ex_factory, style: rec.style, color: rec.color, unitId: unitId}});
          if(!poLine) {
            console.log('PO line doesnt exist for ' + rec.destination + ' ' + rec.ex_factory + ' ' + rec.style + ' ' + rec.color );
            // create the record 
            const poLine = new PoLineEntity();
            poLine.color = rec.color;
            poLine.deliveryDate = rec.ex_factory;
            poLine.destination = rec.destination;
            poLine.style = rec.style;
            poLine.poHeaderId = poHeaderId;
            poLine.poStatus = PoStatusEnum.INPROGRESS;
            poLine.unitId = unitId;
            const savedPoEntity = await poLineEntRepo.save(poLine);

            const poSchEntity = new ScheduleEntity();
            poSchEntity.color = rec.color
            poSchEntity.deliveryDate = rec.ex_factory;
            poSchEntity.destination = rec.destination;
            poSchEntity.endDate = rec.ex_factory;
            poSchEntity.schedule = 'SCHEDULE 1';
            poSchEntity.poHeaderId = poHeaderId;
            poSchEntity.poLineId = savedPoEntity.id;
            poSchEntity.startDate = rec.ex_factory;
            poSchEntity.unitId = unitId;
            poSchEntity.poStatus = PoStatusEnum.INPROGRESS;
            poSchEntity.quantity = rec.quantity;
            poSchEntity.style = rec.style;
            const poSchRec = await poSchEntRepo.save(poSchEntity);


            // save the DC entities for the schedule
            const schDcEntity = new ScheduleDcEntity();
            schDcEntity.unitId = unitId;
            schDcEntity.poHeaderId = poHeaderId;
            schDcEntity.poSchId = poSchRec.id;
            schDcEntity.dcNo = rec.dc_no;
            schDcEntity.quantity = rec.quantity;
            await schDcRepo.save(schDcEntity);
          } else {
            // const key = r.po_number+'@'+r.color+'@'+r.ex_factory;
            const key = poRec.poNumber+'@'+rec.style+'@'+rec.color+'@'+rec.ex_factory+'@'+rec.destination;
            console.log(key);
            const poSchRec = await poSchEntRepo.findOne({select: ['id', 'quantity'], where: { poHeaderId: poHeaderId, destination: rec.destination, deliveryDate: rec.ex_factory, style: rec.style, color: rec.color, unitId: unitId}});
            const existingPoSchQty = poSchRec?.quantity ?? 0;

            // now check if this DC is already mapped to the schedule. If mapped dont add the quantity else add it direclty
            const schDcRec = await schDcRepo.findOne({ select: ['id'], where:  { unitId: unitId, poHeaderId: poHeaderId, poSchId: poSchRec.id, dcNo: rec.dc_no } });
            if(!schDcRec) {
              // const incomingDcQtyForPoColExFact = dcPoColExFactQtyMap.get(key);
              // const diffQty = Number(incomingDcQtyForPoColExFact) - Number(existingPoSchQty);
              // if(diffQty > 0) {
                // await poSchEntRepo.update({poHeaderId: poHeaderId, destination: rec.destination, deliveryDate: rec.ex_factory, unitId: unitId, style: rec.style}, { quantity: () => `quantity + ${Number(incomingDcQtyForPoColExFact)}`});
              // }
              // save the DC entities for the schedule
              const incomingDcQtyForPoColExFact = rec.quantity;

              await poSchEntRepo.update({poHeaderId: poHeaderId, destination: rec.destination, deliveryDate: rec.ex_factory, unitId: unitId, style: rec.style}, { quantity: () => `quantity + ${Number(incomingDcQtyForPoColExFact)}`});
              const schDcEntity = new ScheduleDcEntity();
              schDcEntity.unitId = unitId;
              schDcEntity.poHeaderId = poHeaderId;
              schDcEntity.poSchId = poSchRec.id;
              schDcEntity.dcNo = rec.dc_no;
              schDcEntity.quantity = rec.quantity;
              await schDcRepo.save(schDcEntity);
            }
          }
        } else {
          const poHeaderEnt = new PoHeaderEntity();
          poHeaderEnt.buyerId = buyerId;
          poHeaderEnt.poDescription = rec.po_number;
          poHeaderEnt.poId = rec.po_number;
          poHeaderEnt.poName = rec.po_number;
          poHeaderEnt.poNumber = rec.po_number;
          poHeaderEnt.productId = rec.product_sku;
          poHeaderEnt.productDesc = rec.product_sku;
          // poHeaderEnt.style = rec.style;
          poHeaderEnt.productType = rec.product_sku;
          poHeaderEnt.unitId = unitId;
          poHeaderEnt.poStatus = PoStatusEnum.OPEN;
          const savedPoHeader = await poHeaderEntRepo.save(poHeaderEnt);
          const poHeaderId = savedPoHeader.id;

          // create the record 
          const poLine = new PoLineEntity();
          poLine.color = rec.color;
          poLine.deliveryDate = rec.ex_factory;
          poLine.destination = rec.destination;
          poLine.style = rec.style;
          poLine.poHeaderId = poHeaderId;
          poLine.poStatus = PoStatusEnum.INPROGRESS;
          poLine.unitId = unitId;
          const savedPoLineEntity = await poLineEntRepo.save(poLine);

          const poSchEntity = new ScheduleEntity();
          poSchEntity.color = rec.color
          poSchEntity.deliveryDate = rec.ex_factory;
          poSchEntity.destination = rec.destination;
          poSchEntity.endDate = rec.ex_factory;
          poSchEntity.schedule = 'SCHEDULE 1';
          poSchEntity.poHeaderId = poHeaderId;
          poSchEntity.poLineId = savedPoLineEntity.id;
          poSchEntity.startDate = rec.ex_factory;
          poSchEntity.unitId = unitId;
          poSchEntity.poStatus = PoStatusEnum.INPROGRESS;
          poSchEntity.quantity = rec.quantity;
          poSchEntity.style = rec.style;
          const poSchRec = await poSchEntRepo.save(poSchEntity);

          
          // save the DC entities for the schedule
          const schDcEntity = new ScheduleDcEntity();
          schDcEntity.unitId = unitId;
          schDcEntity.poHeaderId = poHeaderId;
          schDcEntity.poSchId = poSchRec.id;
          schDcEntity.dcNo = rec.dc_no;
          schDcEntity.quantity = rec.quantity;
          await schDcRepo.save(schDcEntity);

        }

      }
      // PO and PO LINES end
      await this.updateTableMigrationStatus(date, hour, unitId, GtsAppTableNamesEnum.M_PO, TableMigrationStatusEnum.COMPLETED, transactionManager);
      await transactionManager.completeTransaction();
      // call the style op creation job
      await this.createStyleOperations(unitId, Array.from(stylesSet));
      // call the transaction info creation job on if the no-transactional-data flag is false
      if(!ntd) {
        await this.createTransactions(unitId, date, hour, '00', '59');
      }
      return new GlobalResponseObject(true, 0 , 'Po info saved successfully');
    } catch(error) {
      await transactionManager.releaseTransaction();
      await this.updateTableMigrationStatusWithError(date, hour, unitId, GtsAppTableNamesEnum.M_PO, TableMigrationStatusEnum.FAILED, error.message?.toString());
      throw error;
    }
  }

  /**
   * 
   * @param unitId 
   * @param date 
   * @param hour 
   * @param fromMinute 
   * @param toMinute 
   * @returns 
   */
  async createTransactions(unitId: string, date: string, hour: number, fromMinute: string, toMinute: string): Promise<GlobalResponseObject> {
    let transactionManager = new GenericTransactionManager(this.dataSource);
    try {
      const stylesSet = new Set<string>();
      const gtsDbConn = await this.gtsDb.getGtsDbConnectionForUnit(DbNamesEnum.SEPL_SCAN, unitId);
      const profitDbConn = await this.gtsDb.getGtsDbConnectionForUnit(DbNamesEnum.PROFIT_DB, unitId);
      // get the po info from the GTS
      let inputDate = new Date(date);
      const currMonth = inputDate.getMonth();
      const currYear = inputDate.getFullYear();

      const alreadyMigrated = await this.helperService.checkIfMigrationIsDoneForTableDateAndHour(date, hour, unitId, GtsAppTableNamesEnum.T_TRANS);
      if(alreadyMigrated) {
        return new GlobalResponseObject(false, 0, GtsAppTableNamesEnum.T_TRANS + 'Table already migrated for the date + hour ' + date + ' : ' + hour);
      }
      await this.saveMigrationLog(date, hour, unitId, GtsAppTableNamesEnum.T_TRANS);

      const trackingEntities: TrackingInfoEntity[] = [];

      // CUT QUANTITY BEGIN
      // For DC real quantities
      const cutCompletedDcQuery = ` SELECT DcNo as dc_no, ItemID  as product_sku, styleID as style, PONo as po_number, styleName as style_desc, Color as color, 
        Destination  as destination, CAST(ExFactoryDate as varchar) as ex_factory,
        BuyerId  as buyer,  count(1) as quantity
        FROM dbo.T_BundlePieces tbp With (NOLOCK) WHERE tbp.DCIssueDate  = '${date}'
        GROUP BY DCNo, ItemID, styleID, Color, PONo, BuyerId, Destination, styleName, ExFactoryDate`;
      const cutCompletedDcResult: {dc_no: string, product_sku: string, style: string, color: string, po_number: string, buyer: string, destination: string, style_desc: string, ex_factory: string, quantity: number, }[] = await profitDbConn.query(cutCompletedDcQuery);

      await transactionManager.startTransaction();
      const transRepo = transactionManager.getRepository(TrackingInfoEntity);
      const phRepoo = transactionManager.getRepository(PoHeaderEntity);
      const poLineRepo = transactionManager.getRepository(PoLineEntity);
      const poSchRepo = transactionManager.getRepository(ScheduleEntity);
      const schDcRepo = transactionManager.getRepository(ScheduleDcEntity);
      if(cutCompletedDcResult?.length > 0) {
        // delete all the records for the specific date and reinsert all again.
        await transRepo.delete({ unitId: unitId, date: date, opCode: '15'});
      }
      // directly insert the records into the transaction table
      for(const rec of cutCompletedDcResult) {
        stylesSet.add(rec.style);
        const poHeader = await phRepoo.findOne({ select: ['id'], where: { poNumber: rec.po_number, unitId: unitId } });
        const phId = poHeader.id;
        // // get the qty that is saved for the DC on the particular date + po + color + ex fact + destination
        const poSch = await poSchRepo.findOne({ select: ['id'], where: { poHeaderId: phId, destination: rec.destination, deliveryDate: rec.ex_factory, color: rec.color, unitId: unitId } });
        let poSchId = poSch.id;

        // create the dc entity for the remaining quantity
        const trackEnt = new TrackingInfoEntity();
        trackEnt.color = rec.color;
        trackEnt.year = currYear;
        trackEnt.date = date;
        trackEnt.hour = hour;
        trackEnt.minute = Number(fromMinute);
        trackEnt.month = currMonth;
        trackEnt.timestamp = 0; // not required for cutting
        trackEnt.opCategory = OperationCategoryEnum.CUTTING;
        trackEnt.opCode = '15';
        trackEnt.opSequence = 1;
        trackEnt.style = rec.style;
        trackEnt.schedule = 'SCHEDULE 1';
        trackEnt.refType = BarcodeTypeEnum.DC;
        trackEnt.refId = rec.dc_no ?? rec.po_number;
        trackEnt.line = 'No line';
        trackEnt.workstation = 'TABLE 1';
        trackEnt.unitId = unitId;
        trackEnt.poHeaderId = phId;
        trackEnt.poScheduleId = poSchId;
        trackEnt.goodQuantity = rec.quantity;
        trackingEntities.push(trackEnt);
      }
      // CUT QUANTITY END


      // WARNING: All the missing PO and PO Lines will be created in here itself as we are direcly working with T_ITEM_TF entity.  All the profit input is present in this table only. So the next sewing transactions will be covered in here
      // SEWING IN START
      const sewInQuery = `SELECT  tit.DCNo as dc_no , tit.ItemID as product_sku, tit.StyleID as style , tit.Color as color, tit.PONo as po_number, 
      tit.BuyerID as buyer, tit.Destination as destination, tit.StyleName as style_desc, count(1) as dc_quantity, 
      tit.OrderDate as order_date, CAST(tit.ExFactoryDate as varchar) as ex_factory
      FROM  SEPL_Scan.dbo.T_Item_TF tit With (NOLOCK)
      WHERE tit.DCIssueDate  = '${date}'
      GROUP BY tit.DCNo , tit.ItemID, tit.StyleID , tit.Color, tit.PONo, tit.BuyerID, tit.Destination, tit.StyleName, tit.OrderDate , tit.ExFactoryDate`;
      const sewInResult: {dc_no: string, product_sku: string, style: string, color: string, po_number: string, buyer: string, destination: string, style_desc: string, ex_factory: string, dc_quantity: number }[] = await gtsDbConn.query(sewInQuery);

      if(sewInResult?.length > 0) {
        // delete all the records for the specific date and reinsert all again.
        await transRepo.delete({ unitId: unitId, date: date, opCode: '129'});
      }
      // SEWING IN END
      for(const rec of sewInResult) {
        stylesSet.add(rec.style);
        const poHeader = await phRepoo.findOne({ select: ['id'], where: { poNumber: rec.po_number, unitId: unitId } });
        let phId = poHeader?.id;
                if(!phId) {
                  let buyerId = 0;
                  // check if the buyer exist
                  const buyer = await transactionManager.getRepository(BuyerEntity).findOne({ select: ['id'], where: {buyerName: rec.buyer, unitId: unitId} });
                  if(!buyer) {
                    const buyerEnt = new BuyerEntity();
                    buyerEnt.buyerCode = rec.buyer;
                    buyerEnt.buyerDesc = rec.buyer;
                    buyerEnt.buyerName = rec.buyer;
                    buyerEnt.unitId = unitId;
                    const savedBuyer = await transactionManager.getRepository(BuyerEntity).save(buyerEnt);
                    buyerId = savedBuyer.id;
                  } else {
                    buyerId = buyer.id;
                  }
                  const poHeaderEnt = new PoHeaderEntity();
                  poHeaderEnt.buyerId = buyerId;
                  poHeaderEnt.poDescription = rec.po_number;
                  poHeaderEnt.poId = rec.po_number;
                  poHeaderEnt.poName = rec.po_number;
                  poHeaderEnt.poNumber = rec.po_number;
                  poHeaderEnt.productId = rec.product_sku;
                  poHeaderEnt.productDesc = rec.product_sku;
                  // poHeaderEnt.style = rec.style;
                  poHeaderEnt.productType = rec.product_sku;
                  poHeaderEnt.unitId = unitId;
                  poHeaderEnt.poStatus = PoStatusEnum.OPEN;
                  const savedPoHeader = await phRepoo.save(poHeaderEnt);
                  phId = savedPoHeader.id;
                  console.log('saved po header');
                }
        // // get the qty that is saved for the DC on the particular date + po + color + ex fact + destination
        const poSch = await poSchRepo.findOne({ select: ['id'], where: { poHeaderId: phId, destination: rec.destination, deliveryDate: rec.ex_factory, color: rec.color, unitId: unitId } });
        let poSchId = poSch?.id;
                if(!poSchId) {
                  // If the po schedule id is not found, then we have to create one

                  // create the record 
                  const poLine = new PoLineEntity();
                  poLine.color = rec.color ?? 'NOCOLOR';
                  poLine.deliveryDate = rec.ex_factory ?? '2000-01-01';
                  poLine.destination = rec.destination ?? 'NOWHERE';
                  poLine.style = rec.style ?? 'NOSTYLE';
                  poLine.poHeaderId = phId;
                  poLine.poStatus = PoStatusEnum.INPROGRESS;
                  poLine.unitId = unitId;
                  const savedPoLineEntity = await poLineRepo.save(poLine);

                  const poSchEntity = new ScheduleEntity();
                  poSchEntity.color = rec.color;
                  poSchEntity.deliveryDate = rec.ex_factory ?? '2000-01-01';
                  poSchEntity.destination = rec.destination ?? 'NODEST';
                  poSchEntity.endDate = rec.ex_factory ?? '2000-01-01';
                  poSchEntity.schedule = 'SCHEDULE 1';
                  poSchEntity.poHeaderId = phId;
                  poSchEntity.poLineId = savedPoLineEntity.id;
                  poSchEntity.startDate = rec.ex_factory ?? '2000-01-01';
                  poSchEntity.unitId = unitId;
                  poSchEntity.poStatus = PoStatusEnum.INPROGRESS;
                  poSchEntity.quantity = rec.dc_quantity;
                  poSchEntity.style = rec.style ?? 'NOSTYLE';
                  const savedPoSch = await poSchRepo.save(poSchEntity);
                  poSchId = savedPoSch.id;


                   // save the DC entities for the schedule
                  const schDcEntity = new ScheduleDcEntity();
                  schDcEntity.unitId = unitId;
                  schDcEntity.poHeaderId = phId;
                  schDcEntity.poSchId = poSchId;
                  schDcEntity.dcNo = rec.dc_no;
                  schDcEntity.quantity = rec.dc_quantity;
                  await schDcRepo.save(schDcEntity);

                  console.log('saved po line');
                }
        
        if(!poSchId || !phId) {
          throw new ErrorResponse(0, 'Po and Po schedule id are not found');
        }
        // create the dc entity for the remaining quantity
        const trackEnt = new TrackingInfoEntity();
        trackEnt.color = rec.color;
        trackEnt.year = currYear;
        trackEnt.date = date;
        trackEnt.hour = hour;
        trackEnt.minute = Number(fromMinute);
        trackEnt.month = currMonth;
        trackEnt.timestamp = 0; // not required for cutting
        trackEnt.opCategory = OperationCategoryEnum.SEWING;
        trackEnt.opCode = '129';
        trackEnt.opSequence = 2;
        trackEnt.style = rec.style;
        trackEnt.schedule = 'SCHEDULE 1';
        trackEnt.refType = BarcodeTypeEnum.DC;
        trackEnt.refId = rec.dc_no ?? rec.po_number;
        trackEnt.line = 'No line';
        trackEnt.workstation = 'TABLE 1';
        trackEnt.unitId = unitId;
        trackEnt.poHeaderId = phId;
        trackEnt.poScheduleId = poSchId;
        trackEnt.goodQuantity = rec.dc_quantity;
        trackingEntities.push(trackEnt);

        console.log('saved tracking entity');
      }
      console.log('SE IN DONE');
      // SEWING OUT, FIN, WASH START
      const appender = hour ? `tit.UserHour = '${hour}' AND tit.UserDate = '${date}' `:  `tit.UserDate = '${date}' `;
      const allOpsQuery = `SELECT
        tit.BuyerId as buyer, tit.PONo as po_no, tit.StyleID as style, tit.Color as color, tit.Destination as destination, CAST(tit.ExFactoryDate as varchar) as ex_factory, 
        tit.ItemID as product_sku, tit.SL_ID as op_code, tit.batch as batch, SUM(tit.Quantity)  as quantity, UserHour as scan_hour
        FROM SEPL_Scan.dbo.T_Scan_TF_Summary tit  
        WHERE ${appender}
	 	    GROUP BY tit.BuyerId, tit.PONo , tit.StyleID, tit.Color, tit.Destination , tit.ExFactoryDate , tit.ItemID, tit.SL_ID, tit.batch, UserHour`;
      const allOpsResult: {buyer: string, po_no: string,	style: string, color: string, destination: string, ex_factory: string, product_sku: string, op_code: string, quantity: number, batch: string, scan_hour: string }[] = await gtsDbConn.query(allOpsQuery);
      console.log(allOpsQuery);
      console.log(allOpsResult);
      const opInfo = await this.getOpInfo(unitId);
      for(const rec of allOpsResult) {
        stylesSet.add(rec.style);
        const poHeader = await phRepoo.findOne({ select: ['id'], where: { poNumber: rec.po_no, unitId: unitId } });
        let phId = poHeader?.id;
        const poSch = await poSchRepo.findOne({ select: ['id'], where: { poHeaderId: phId, destination: rec.destination, deliveryDate: rec.ex_factory, color: rec.color, unitId: unitId } });
        let poSchId = poSch?.id;

        const currOpInfo = opInfo.get(rec.op_code.toString());
        if(phId && poSchId) {
          const trackEnt = new TrackingInfoEntity();
          trackEnt.color = rec.color;
          trackEnt.unitId = unitId;
          trackEnt.year = currYear;
          trackEnt.date = date;
          trackEnt.hour = hour;
          trackEnt.minute = Number(fromMinute);
          trackEnt.month = currMonth;
          trackEnt.opCategory = currOpInfo?.opCategory ?? OperationCategoryEnum.PACKING;
          trackEnt.opCode = rec.op_code;
          trackEnt.opSequence = currOpInfo?.opSequence ?? 0;
          trackEnt.style = rec.style;
          trackEnt.schedule = 'SCHEDULE 1';
          trackEnt.refId = 'NONE';
          trackEnt.line = rec.batch;
          trackEnt.refType = BarcodeTypeEnum.BUN;
          trackEnt.poHeaderId = phId;
          trackEnt.poScheduleId = poSchId;
          trackEnt.goodQuantity = rec.quantity;
          const scanDate = date+` ${rec.scan_hour}:00:00`;
          trackEnt.timestamp = new Date(scanDate).getTime();
          trackingEntities.push(trackEnt);
        } else {
          const obj = {
            po: rec.po_no, destination: rec.destination, del: rec.ex_factory, poHeader: phId
          }
          console.log(obj);
        }
      }
      console.log('done');
      await transactionManager.getRepository(TrackingInfoEntity).save(trackingEntities, {reload: false});
      await this.updateTableMigrationStatus(date, hour, unitId, GtsAppTableNamesEnum.T_TRANS, TableMigrationStatusEnum.COMPLETED, transactionManager);
      await transactionManager.completeTransaction();
      await this.createStyleOperations(unitId, Array.from(stylesSet));
      // update the migration log 
      return new GlobalResponseObject(true, 0 , `Tracking info saved successfully for the hour ${date} and ${hour} `);
    } catch (error) {
      await transactionManager.releaseTransaction();
      await this.updateTableMigrationStatusWithError(date, hour, unitId, GtsAppTableNamesEnum.T_TRANS, TableMigrationStatusEnum.FAILED, error.message?.toString());
      console.log(error);
    }
  }


  // helper for getting the ops info
  async getOpInfo(unitId: string): Promise<Map<string, OperationEntity>> {
    const opsInfoMap = new Map<string, OperationEntity>();
    const ops = await this.opEnt.find({ select: ['id', 'opCategory', 'opCode', 'opName', 'opSequence'], where: { unitId: unitId } });
    ops.forEach(rec => {
      opsInfoMap.set(rec.opCode, rec);
    });
    return opsInfoMap;
  }

  async updateTableMigrationStatus(date: string, hour: number, unit: string, tableName: GtsAppTableNamesEnum, status: TableMigrationStatusEnum, transactionManager?: GenericTransactionManager) : Promise<boolean> {
    if(transactionManager) {
      await transactionManager.getRepository(MigrationLogEntity).update({unitId: unit, migrationDate: date, hour: hour, tableName: tableName}, {status: status});
    } else {
      await this.migLogEnt.update({unitId: unit, migrationDate: date, hour: hour, tableName: tableName}, {status: status});
    }
    return true;
  }

  async updateTableMigrationStatusWithError(date: string, hour: number, unit: string, tableName: GtsAppTableNamesEnum, status: TableMigrationStatusEnum, erroMessage: string, transactionManager?: GenericTransactionManager) : Promise<boolean> {
    if(transactionManager) {
      await transactionManager.getRepository(MigrationLogEntity).update({unitId: unit, migrationDate: date, hour: hour, tableName: tableName}, {status: status, failReason: erroMessage});
    } else {
      await this.migLogEnt.update({unitId: unit, migrationDate: date, hour: hour, tableName: tableName}, {status: status, failReason: erroMessage});
    }
    return true;
  }

  async saveMigrationLog(date: string, hour: number, unit: string, tableName: GtsAppTableNamesEnum, transactionManager?: GenericTransactionManager): Promise<boolean> {
    const migLog = new MigrationLogEntity();
    migLog.tableName = tableName;
    migLog.hour = hour;
    migLog.unitId = unit;
    migLog.migrationDate = date;
    migLog.status = TableMigrationStatusEnum.OPEN;
    console.log(migLog);
    if(transactionManager) {
      await transactionManager.getRepository(MigrationLogEntity).save(migLog, {reload: false});
    } else {
      await this.migLogEnt.save(migLog, {reload: false});
    }
    return true;
  }
}


